import React, { Component } from 'react';
import { connect } from 'react-redux';
// components
import { Row, Col, Button, Input } from 'reactstrap';
import { InlineSync, FontAwesome } from 'components/ui';
import { BaseSelect, PlatoonSelect, SoldierSelect, ParshaSelect } from 'components/inputs';
// functions
import { toast } from 'react-toastify';
import { isAdmin } from 'functions/login';
import { julianToday } from 'functions/dates';
// store and constants
import { getMissions } from 'store/missions/mark/operations';
import { LEGACY_URL, SERIAL_NUMBER_REGEX, SERIAL_NUMBER_PATTERN } from 'components/constants';

export class MissionsForm extends Component {

  state = {
    school_id: false, class_id: false,
    parsha_id: false,
    // The serial box is controlled so it always shows the selected soldier's
    // serial. serialStatus is 'loading' while a typed serial waits for the
    // soldiers list, and 'not-found' when a complete serial matched nobody.
    serial: '', serialStatus: null,
  };

  componentDidMount() {
    // set the default school id and class id
    const { school_id } = this.props.login;
    this.setState({ school_id });
    // set the default parsha
    this.setDefaultParsha();
  }

  componentDidUpdate( prevProps ) {
    if ( prevProps.parshos !== this.props.parshos )
      this.setDefaultParsha();
    // A serial typed (or scanned) before the soldiers list arrived is still
    // waiting — look it up now. Only while it is pending: once resolved, a
    // later reload (a platoon change refetches the list) must not override a
    // soldier the user has since picked by hand.
    if ( prevProps.soldiers !== this.props.soldiers && this.state.serialStatus === 'loading' )
      this.resolveSerial( this.state.serial );
  }

  setDefaultParsha() {
    const { parshos } = this.props;

    if ( parshos.length === 0 )
      return false;

    const today = julianToday();
    // select the most recently completed parsha
    let parsha = parshos.filter( parsha => parsha.end < today ).pop();

    if ( !parsha ) {
      parsha = parshos[0]; // if we can't find a parsha, choose first one
    }
    this.setState({ parsha_id: parsha.id });
  }

  onSelectChange = key => option => this.setState({ [key]: option ? option.value : false });
  onPlatoonChange = option => {
    // Changing/clearing platoon must not wipe the chosen soldier
    this.setState({ class_id: option ? option.value : false });
  }
  // Chosen from the dropdown (or cleared by it). Put the soldier's serial in
  // the serial box so the two can never disagree: a serial sitting beside a
  // different child's name is exactly what made this page look like it had
  // looked up the wrong soldier.
  onSoldierChange = option => {
    const user_id = option && option.value ? option.value : false;
    const soldier = user_id
      && this.props.soldiers.find( s => String( s.user_id ) === String( user_id ) );
    this.setState({ serial: soldier ? String( soldier.user_serial ) : '', serialStatus: null });
    this.props.onSoldierChange( user_id );
  }

  selectSerial = ({ target }) => {
    this.setState({ serial: target.value });
    this.resolveSerial( target.value );
  }

  // Look a serial up in the soldiers list. This used to `return false` on
  // every path except a match, so a lookup that failed changed nothing and
  // left whichever child was already showing — and nothing ever retried it.
  // Every outcome now updates the page.
  resolveSerial = value => {
    if ( !value.match( SERIAL_NUMBER_REGEX ) ) {
      // Still typing: leave the current soldier alone.
      this.setState({ serialStatus: null });
      return;
    }
    // The list is still loading. Hold the serial; componentDidUpdate retries.
    if ( this.props.soldiers.length === 0 ) {
      this.setState({ serialStatus: 'loading' });
      return;
    }
    const serial_number = parseInt( value, 10 );
    const soldier = this.props.soldiers.find( s => s.user_serial === serial_number );
    if ( !soldier ) {
      // Clear rather than keep the previous child on screen beside a serial
      // that is not theirs.
      this.setState({ serialStatus: 'not-found' });
      this.props.onSoldierChange( false );
      return;
    }
    // load his missions
    this.setState({ serialStatus: null });
    this.props.onSoldierChange( soldier.user_id );
    this.setState({
      class_id: soldier.class_id,
      school_id: soldier.school_id,
    }, () => this.loadMissions( soldier.user_id ) );
  }

  // Takes the soldier explicitly when it is known. Reading this.props.user_id
  // relied on the parent having already re-rendered with the new soldier by
  // the time this setState callback ran — true under React 16's batching, but
  // exactly the kind of timing that loads the previous child's missions if it
  // ever slips.
  loadMissions = ( user_id = this.props.user_id ) => {
    if ( !this.props.loading )
      this.props.getMissions( user_id, this.state.parsha_id )
      .catch( e => toast.error( e.message ) );
  }

  render() {
    let { school_id, class_id, parsha_id, serial, serialStatus } = this.state;
    const today = julianToday();
    const { login, loading, user_id } = this.props;
    const isCamp = login.code === 'CAMP';
    const who = isCamp ? 'camper' : 'soldier';

    return (
      <div id='BCMissionsForm'>

        <Row>
          <Col>
            <label>Serial Number (Quick Select)</label>
            <Input type='text' pattern={SERIAL_NUMBER_PATTERN} autoFocus
              value={ serial }
              onChange={ this.selectSerial } />
            {/* Must stay the input's next sibling: styles/index.css reveals it
                with `input:invalid + div.invalid-message`. */}
            <div className='invalid-message'>Please enter a valid serial number (e.g. 7765904)</div>
            { serialStatus === 'loading' &&
              <div className='form-text text-muted'>
                Loading { who }s — serial { serial } will be looked up as soon as the list arrives.
              </div> }
            { serialStatus === 'not-found' &&
              <div className='form-text text-danger'>
                No registered { who } with serial { serial } in the { who }s you can see.
                They may not be registered for this year, or may belong to another { isCamp ? 'camp' : 'base' }.
              </div> }
          </Col>
        </Row>

        <hr/>

        <form target='_blank' method='post' action={`${LEGACY_URL}/api/print/mark`}>
          <Row>
            <Col sm={6}>
              <label>{ isCamp ? 'Camp' : 'Base' }</label>
              <BaseSelect
                name='school_id'
                value={ school_id }
                isDisabled={ !isAdmin( login.code ) }
                onChange={ this.onSelectChange('school_id') } />
              <input type='hidden'  value={ school_id || '' } name='school_id' />
            </Col>

            <Col sm={6}>
              <label>{ isCamp ? 'Bunk' : 'Platoon' }</label>
              <PlatoonSelect
                isClearable
                value={ class_id }
                schoolId={ school_id }
                openMenuOnFocus={ false }
                onChange={ this.onPlatoonChange } />
              <input type='hidden' value={ class_id || '' } name='class_id' />
            </Col>

            <Col sm={6}>
              <label>{ isCamp ? 'Camper' : 'Soldier' }</label>
              {/* isClearable stops SoldierSelect auto-selecting the first
                  soldier in the list whenever none is chosen — on a marking
                  page that put an arbitrary child on screen, and missions
                  could then be marked against them. */}
              <SoldierSelect
                isClearable
                registeredOnly={ !isCamp }
                value={ user_id }
                classId={ class_id }
                schoolId={ school_id }
                openMenuOnFocus={ false }
                onChange={ this.onSoldierChange } />
              <input type='hidden' value={ user_id || '' } name='user_id' />
            </Col>

            <Col sm={6}>
              <label>Parsha</label>
              <ParshaSelect
                isClearable
                isDescending
                value={ parsha_id }
                endDate={ today } // only show completed weeks
                onChange={ this.onSelectChange('parsha_id') } />
              <input type='hidden' value={ parsha_id || '' } name='parsha_id'/>
            </Col>
          </Row>

          <Row className='buttons'>
            <Col sm={6}>
              {/* Wrapped: onClick would pass the click event as user_id. */}
              <Button color='primary' onClick={ () => this.loadMissions() }
                  disabled={ !user_id || !parsha_id || loading } >
                <InlineSync loading={ loading } /> Load Missions
              </Button>
            </Col>

            <Col sm={6}>
              <Button color='primary' disabled={ !user_id || !parsha_id }>
                <FontAwesome icon='print'/> Mark Printed Version
              </Button>
            </Col>
          </Row>
        </form>
      </div>
    );
  }
}

const mapStateToProps = ({ login, missions, base }) => {
  return {
    login: login.current_login,
    loading: missions.mark.loading,
    soldiers: base.soldiers.soldiers,
    parshos: missions.parshos.parshos,
  }
};

const mapDispatchToProps = { getMissions };

export default connect( mapStateToProps, mapDispatchToProps )( MissionsForm );



// WEBPACK FOOTER //
// ./src/pages/missions/mark/includes/MissionsForm.jsx